import { Observable } from 'rxjs';

import {
  AudienceFilterParams,
  AudienceSystemsResponse,
  CampaignPreview,
  AudiencePreview,
  CreateMailingCampaignPayload,
  CreateMailingTemplatePayload,
  FolioResolution,
  FolioSourcePayload,
  GetAudienceSystemsParams,
  GetMailingCampaignsParams,
  GetMailingRecipientsParams,
  MailingAsset,
  MailingCampaign,
  MailingCampaignsResponse,
  MailingFolioList,
  MailingFolioListDetail,
  MailingFolioListsResponse,
  MailingPreviewPayload,
  MailingRecipientsResponse,
  MailingTemplate,
  MailingTemplatesResponse,
  RenderedEmail,
  ReserveMailingAssetPayload,
  ReservedMailingAsset,
  SaveFolioListPayload,
  SavedFolioList,
  SendTestResult,
  UpdateMailingCampaignPayload,
  UpdateMailingTemplatePayload,
} from '../../../interfaces/mailing.interface';

/**
 * Único punto de acceso a datos del mailing a clientes (`/mailing`).
 *
 * Clase abstracta y no interfaz por la misma razón que `ItTicketsApiService`:
 * Angular necesita un token de inyección en tiempo de ejecución. Los
 * componentes piden esta clase y `mailing.provider.ts` decide la
 * implementación.
 */
export abstract class MailingApiService {
  // ── Plantillas ───────────────────────────────────────────────────────

  /** Plantillas activas; `includeInactive` suma las dadas de baja */
  abstract getTemplates(includeInactive?: boolean): Observable<MailingTemplatesResponse>;

  abstract createTemplate(payload: CreateMailingTemplatePayload): Observable<MailingTemplate>;

  abstract updateTemplate(
    templateId: number,
    payload: UpdateMailingTemplatePayload,
  ): Observable<MailingTemplate>;

  /** Borrado lógico; devuelve la plantilla tal como estaba */
  abstract deleteTemplate(templateId: number): Observable<MailingTemplate>;

  // ── Campañas ─────────────────────────────────────────────────────────

  abstract getCampaigns(params: GetMailingCampaignsParams): Observable<MailingCampaignsResponse>;

  /** Detalle con el cuerpo y los conteos por destinatario */
  abstract getCampaign(campaignId: number): Observable<MailingCampaign>;

  abstract createCampaign(payload: CreateMailingCampaignPayload): Observable<MailingCampaign>;

  /** Solo mientras es borrador; el backend responde 409 si ya se encoló */
  abstract updateCampaign(
    campaignId: number,
    payload: UpdateMailingCampaignPayload,
  ): Observable<MailingCampaign>;

  /** Solo borradores. Devuelve la campaña ya en `cancelled`. */
  abstract deleteCampaign(campaignId: number): Observable<MailingCampaign>;

  abstract getRecipients(
    campaignId: number,
    params: GetMailingRecipientsParams,
  ): Observable<MailingRecipientsResponse>;

  /**
   * Confirma el envío: el backend congela la campaña, toma el snapshot de
   * destinatarios y dispara el fan-out. Devuelve la campaña ya en `queued`.
   * 422 `AUDIENCE_EMPTY` si nadie de la audiencia tiene correo.
   */
  abstract sendCampaign(campaignId: number): Observable<MailingCampaign>;

  /**
   * Correo completo de una campaña guardada. Con `recipientId` se rinde con
   * las variables congeladas de ese destinatario, que son las que de verdad
   * se usaron al enviar. Pide `view_mailings`, no `send_mailings`: es una
   * lectura de lo que ya existe.
   */
  abstract previewCampaign(campaignId: number, recipientId?: number): Observable<CampaignPreview>;

  // ── Audiencia, vista previa y prueba ─────────────────────────────────

  /** Conteos y muestra de los sistemas que alcanzan los filtros */
  abstract previewAudience(
    filters: AudienceFilterParams,
    sampleSize?: number,
  ): Observable<AudiencePreview>;

  /**
   * La audiencia completa, paginada y con el correo de cada sistema: a quién
   * le va a llegar y a quién no.
   *
   * Se pide aquí y no a `GET /systems` porque ese listado no trae el correo,
   * que es justo lo que decide quién recibe y quién queda `skipped`; y porque
   * ésta es la MISMA consulta que arma el snapshot al encolar.
   *
   * Los filtros y `listId` son excluyentes: el backend responde 422 si llegan
   * los dos.
   */
  abstract getAudienceSystems(
    params: GetAudienceSystemsParams,
  ): Observable<AudienceSystemsResponse>;

  /** HTML completo del correo con las variables de un sistema real */
  abstract previewEmail(payload: MailingPreviewPayload): Observable<RenderedEmail>;

  /** Manda el correo rendido al buzón del usuario en sesión */
  abstract sendTestEmail(payload: MailingPreviewPayload): Observable<SendTestResult>;

  // ── Listas de folios ─────────────────────────────────────────────────

  /** Listas guardadas, con `search`, `page` y `limit` */
  abstract getFolioLists(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Observable<MailingFolioListsResponse>;

  /** Detalle con los renglones paginados y los folios que no resolvieron */
  abstract getFolioList(
    listId: number,
    params: { page?: number; limit?: number },
  ): Observable<MailingFolioListDetail>;

  /**
   * Parsea y resuelve el archivo **sin guardar nada**: el reporte que se
   * enseña antes de confirmar la carga.
   */
  abstract previewFolioList(payload: FolioSourcePayload): Observable<FolioResolution>;

  /** Devuelve la lista y, plano a su lado, el reporte de la carga */
  abstract createFolioList(payload: SaveFolioListPayload): Observable<SavedFolioList>;

  /**
   * Renombra; si el payload trae `folios` o `fileBase64` **reemplaza** el
   * contenido completo de la lista, no lo suma. Sin contenido nuevo la
   * respuesta viene sin reporte: no se resolvió nada.
   */
  abstract updateFolioList(
    listId: number,
    payload: Partial<SaveFolioListPayload>,
  ): Observable<SavedFolioList>;

  /** Borrado lógico. 409 si una campaña en vuelo la está usando. */
  abstract deleteFolioList(listId: number): Observable<MailingFolioList>;

  /** Conteos y muestra de los sistemas de la lista; mismo shape que `previewAudience` */
  abstract previewListAudience(listId: number, sampleSize?: number): Observable<AudiencePreview>;

  // ── Imágenes (tres pasos, como los adjuntos de tickets) ───────────────

  /** Paso 1: reservar y pedir la URL prefirmada */
  abstract reserveAsset(payload: ReserveMailingAssetPayload): Observable<ReservedMailingAsset>;

  /**
   * Paso 2: `PUT` directo a S3. El `Content-Type` debe ser exactamente el
   * declarado en el paso 1: la firma va atada a él.
   */
  abstract uploadToPresignedUrl(uploadUrl: string, file: File): Observable<void>;

  /** Paso 3: confirmar; sin esto el asset queda `pending` */
  abstract confirmAsset(assetId: number): Observable<MailingAsset>;
}
